import React, { useState } from 'react';
import type { MoFamily } from './types';
import { moFamilyStore } from '../../stores/moFamilyStore';

interface InviteTabProps {
 selectedFamily: MoFamily;
 onTabChange: (tab: string) => void;
}

export const InviteTab: React.FC<InviteTabProps> = ({
 selectedFamily,
 onTabChange,
}) => {
 const [inviteEmail, setInviteEmail] = useState('');
 const [searchResults, setSearchResults] = useState<any[]>([]);
 const [showSearchDropdown, setShowSearchDropdown] = useState(false);
 const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
 const [selectedPeer, setSelectedPeer] = useState<any | null>(null);
 const [rootFamilies, setRootFamilies] = useState<any[]>([]);
 const [selectedRootFamily, setSelectedRootFamily] = useState<any | null>(null);
 const [showFamilySelection, setShowFamilySelection] = useState(false);
 const [inviteForm, setInviteForm] = useState({
  proposedSlots: 1,
  proposedPrice: 0,
  message: ''
 });
 const [slotDelegation, setSlotDelegation] = useState<('none' | 'dedicated' | 'shared')[]>([]);
 const [dedicatedSlots, setDedicatedSlots] = useState<number[]>([]);
 // 영상 박스가 구동 중인 슬롯 번호 — 자동 선택에서 제외, 명시 선택 시 확인 문구.
 const [boxBoundSlots, setBoxBoundSlots] = useState<number[]>([]);
 const [selectedRootFamilySlots, setSelectedRootFamilySlots] = useState<number[]>([]);
 const [rootFamilySlotsData, setRootFamilySlotsData] = useState<any[]>([]);

 // Search users for invitation
 const searchUsers = async (query: string) => {
  if (query.length < 2) {
   setSearchResults([]);
   setShowSearchDropdown(false);
   return;
  }

  if (searchTimeout) clearTimeout(searchTimeout);

  const timeout = setTimeout(async () => {
   try {
    const response = await fetch(`/mopeer/search?q=${encodeURIComponent(query)}`, {
     credentials: 'include'
    });

    if (response.ok) {
     const data = await response.json();
     setSearchResults(data.data || []);
     setShowSearchDropdown(true);
    }
   } catch (error) {
    console.error('Search error:', error);
   }
  }, 300);

  setSearchTimeout(timeout);
 };

 // Load root families for selected peer
 const loadRootFamilies = async (peer: any) => {
  try {
   const response = await fetch(`/mofamily/owner/${peer.moPeerHash}/root-families`, {
    credentials: 'include'
   });

   if (response.ok) {
    const data = await response.json();
    setRootFamilies(data.data || []);
    setShowFamilySelection(true);
   }
  } catch (error) {
   console.error('Error loading root families:', error);
  }
 };

 // Select a peer
 const selectPeer = (peer: any) => {
  setSelectedPeer(peer);
  setInviteEmail(peer.email);
  setShowSearchDropdown(false);
  loadRootFamilies(peer);
 };

 // Initialize slot delegation array when family is selected
 const initializeSlotDelegation = (totalSlots: number) => {
  setSlotDelegation(new Array(totalSlots || 10).fill('none'));
 };

 // Handle slot click
 const handleSlotClick = async (index: number) => {
  const newDelegation = [...slotDelegation];
  const currentState = newDelegation[index];
  const slotNumber = index + 1;

  // Cycle: none -> dedicated -> shared -> none
  if (currentState === 'none') {
   // 박스 구동 슬롯의 명시 선택 — 위임이 성립되면 박스 바인딩이 해제됨을 확인받는다.
   const actualSlotNumber = selectedRootFamilySlots[index] ?? slotNumber;
   if (boxBoundSlots.includes(actualSlotNumber)
     && !window.confirm(`슬롯 ${actualSlotNumber}은(는) 영상 박스가 사용 중입니다. 위임이 성립되면 이 슬롯의 박스 편성이 해제됩니다. 계속할까요?`)) {
    return;
   }
   // 초대 단계에서는 위임 체인 확인이 불가능 (아직 관계가 없음)
   // 나중에 실제 위임 처리 시 확인하도록 함
   newDelegation[index] = 'dedicated';
  } else if (currentState === 'dedicated') {
   newDelegation[index] = 'shared';
  } else {
   newDelegation[index] = 'none';
  }

  setSlotDelegation(newDelegation);

  // Update proposed slots count
  const delegatedCount = newDelegation.filter(s => s !== 'none').length;
  setInviteForm({ ...inviteForm, proposedSlots: delegatedCount });
 };

 // Invite member
 const inviteMember = async () => {
  if (!selectedRootFamily || !selectedFamily || inviteForm.proposedSlots === 0) {
   alert('초대할 모패밀리와 슬롯을 선택해주세요.');
   return;
  }

  console.log('Inviting - selectedFamily:', selectedFamily);
  console.log('Inviting - selectedRootFamily:', selectedRootFamily);

  // 독점 및 공유 슬롯 번호 추출
  const dedicatedSlotNumbers: number[] = [];
  const sharedSlotNumbers: number[] = [];

  slotDelegation.forEach((state, index) => {
   if (state === 'dedicated') {
    // Use actual slot number if available, otherwise use index + 1
    const slotNumber = selectedRootFamilySlots.length > index ? selectedRootFamilySlots[index] : index + 1;
    dedicatedSlotNumbers.push(slotNumber);
   } else if (state === 'shared') {
    const slotNumber = selectedRootFamilySlots.length > index ? selectedRootFamilySlots[index] : index + 1;
    sharedSlotNumbers.push(slotNumber);
   }
  });

  // sharingType 결정: 모두 독점이면 'dedicated', 아니면 'shared'
  const sharingType = sharedSlotNumbers.length === 0 && dedicatedSlotNumbers.length > 0 ? 'dedicated' : 'shared';

  const inviteUrl = `/mofamily/${selectedFamily.moFamilyHash}/invite-family`;
  console.log('Invite URL:', inviteUrl);
  console.log('Invitation slots:', {
   dedicatedSlots: dedicatedSlotNumbers,
   sharedSlots: sharedSlotNumbers,
   selectedRootFamilySlots,
   slotDelegation,
   proposedSlots: inviteForm.proposedSlots
  });

  try {
   // 모패밀리 간 초대 API 호출
   const response = await fetch(inviteUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
     toMoFamilyHash: selectedRootFamily.moFamilyHash,
     recipientEmail: selectedPeer.email,
     proposedSlots: inviteForm.proposedSlots,
     proposedPrice: inviteForm.proposedPrice,
     sharingType: sharingType,
     dedicatedSlots: dedicatedSlotNumbers,
     sharedSlots: sharedSlotNumbers,
     message: inviteForm.message
    })
   });

   if (response.ok) {
    const result = await response.json();

    // 자신의 모패밀리를 초대한 경우 바로 계층도 탭으로 이동
    if (result.data?.message?.includes('자동으로 수락')) {
     alert('자신의 모패밀리에 대한 초대가 자동으로 수락되었습니다.');

     // Reset all states
     setInviteEmail('');
     setSelectedPeer(null);
     setSelectedRootFamily(null);
     setRootFamilies([]);
     setShowFamilySelection(false);
     setInviteForm({
      proposedSlots: 0,
      proposedPrice: 0,
      sharingType: 'shared',
      message: ''
     } as any);
     setSlotDelegation([]);

     // 계층도 탭으로 전환
     onTabChange('hierarchy');

     // 계층도 리로드 트리거
     moFamilyStore.getState().setHierarchyNeedsReload(true);
    } else {
     alert('초대가 완료되었습니다!');
     // Reset all states
     setInviteEmail('');
     setSelectedPeer(null);
     setSelectedRootFamily(null);
     setRootFamilies([]);
     setShowFamilySelection(false);
     setInviteForm({
      proposedSlots: 0,
      proposedPrice: 0,
      sharingType: 'shared',
      message: ''
     } as any);
     setSlotDelegation([]);
    }
   } else {
    const error = await response.json();
    alert(error.error || '초대에 실패했습니다.');
   }
  } catch (error) {
   alert('초대 중 오류가 발생했습니다.');
  }
 };

 return (
  <div className="space-y-4">
   {selectedFamily?.maxMembers === 1 ? (
    <div className="bg-gray-50 p-8 text-center">
     <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
     </svg>
     <h3 className="text-lg font-medium text-gray-900 mb-2">개인 전용 모패밀리</h3>
     <p className="text-gray-600">기본 모패밀리는 개인 전용으로 다른 모패밀리를 초대할 수 없습니다.</p>
     <p className="text-sm text-gray-500 mt-2">단, 다른 모패밀리의 초대를 받아 하위로 들어갈 수는 있습니다.</p>
     <p className="text-sm text-gray-500 mt-1">하위 모패밀리를 초대하려면 새로운 모패밀리를 만들어주세요.</p>
    </div>
   ) : (
    <>
     <div className="search-container relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
       초대할 모피어 검색
      </label>
      <input
       type="email"
       value={inviteEmail}
       onChange={(e) => {
        setInviteEmail(e.target.value);
        searchUsers(e.target.value);
       }}
       placeholder="모피어 이름 또는 이메일로 검색..."
       className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {showSearchDropdown && searchResults.length > 0 && (
       <div className="absolute z-10 w-full mt-1 bg-white border max-h-60 overflow-auto">
        {searchResults.map((user) => (
         <div
          key={user.moPeerHash}
          className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
          onClick={() => selectPeer(user)}
         >
          <div className="flex items-center gap-3">
           <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm">
            {user.profilePicture ? (
             <img src={user.profilePicture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
             (user.name || user.email).charAt(0).toUpperCase()
            )}
           </div>
           <div>
            <div className="text-sm font-medium">{user.name || '이름 없음'}</div>
            <div className="text-xs text-gray-500">{user.email}</div>
           </div>
          </div>
         </div>
        ))}
       </div>
      )}
     </div>

     {/* 선택된 모피어 표시 */}
     {selectedPeer && (
      <div className="bg-blue-50 p-3">
       <p className="text-sm text-blue-800">
        선택된 모피어: <strong>{selectedPeer.name || selectedPeer.email}</strong>
       </p>
      </div>
     )}

     {/* 루트 패밀리 선택 */}
     {showFamilySelection && rootFamilies.length > 0 && (
      <div className="space-y-3">
       <label className="block text-sm font-medium text-gray-700">
        초대할 모패밀리 선택
       </label>
       <div className="space-y-2 max-h-48 overflow-y-auto border p-3">
        {rootFamilies.map((family) => (
         <div
          key={family.moFamilyHash}
          className={`p-3 cursor-pointer transition-colors ${
           selectedRootFamily?.moFamilyHash === family.moFamilyHash
            ? 'bg-yellow-100 border-yellow-300 border'
            : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
          }`}
          onClick={async () => {
           setSelectedRootFamily(family);

           // Get actual slot numbers and authorization info for the selected family
           let actualSlots: any[] = [];
           try {
            const slotsResponse = await fetch(`/mofamily/${family.moFamilyHash}/slots`, {
             credentials: 'include'
            });
            if (slotsResponse.ok) {
             const slotsData = await slotsResponse.json();
             actualSlots = slotsData.data || [];
             setSelectedRootFamilySlots(actualSlots.map((slot: any) => slot.slot_number));
             setRootFamilySlotsData(actualSlots);
            }
           } catch (error) {
            console.error('Error fetching slots:', error);
            setRootFamilySlotsData([]);
           }

           // Check which slots are already delegated as dedicated
           let alreadyDedicated: number[] = [];
           try {
            const response = await fetch(`/mofamily/${selectedFamily?.moFamilyHash}/dedicated-slots`, {
             credentials: 'include'
            });
            if (response.ok) {
             const data = await response.json();
             alreadyDedicated = data.data.dedicatedSlots || [];
            }
           } catch (error) {
            console.error('Error fetching dedicated slots:', error);
           }
           setDedicatedSlots(alreadyDedicated);

           // 영상 박스 구동 슬롯 — 자동 선택 제외 대상(위임 성립 시 박스 바인딩이 해제되므로).
           let boxBound: number[] = [];
           try {
            const br = await fetch(`/videobox/bindings/${family.moFamilyHash}`, { credentials: 'include' });
            if (br.ok) {
             const bj = await br.json();
             boxBound = (bj.data || []).map((b: any) => b.slot_number);
            }
           } catch { /* 정보 없으면 제외 없이 진행 */ }
           setBoxBoundSlots(boxBound);

           // 기본값: 첫 위임 가능 슬롯 1개를 **독점**으로 미리 선택(2026-08-26).
           // 무위임 초대는 "관계만 있고 배포가 안 흐르는" 흔한 함정이라, 켜진 상태로
           // 시작하고 원치 않으면 클릭으로 해제(shared→none 순환)하게 한다.
           // 이미 위임됐거나 박스가 구동 중인 슬롯은 건너뛴다.
           const slotCount = actualSlots.length || family.slotCount || 10;
           const newDelegation: ('none' | 'dedicated' | 'shared')[] = new Array(slotCount).fill('none');
           const usable = (n: number) => !alreadyDedicated.includes(n) && !boxBound.includes(n);
           const defaultIdx = actualSlots.length
            ? actualSlots.findIndex((s: any) => usable(s.slot_number))
            : [...Array(slotCount).keys()].find(i => usable(i + 1)) ?? -1;
           if (defaultIdx >= 0) newDelegation[defaultIdx] = 'dedicated';
           setSlotDelegation(newDelegation);
          }}
         >
          <div className="flex justify-between items-center">
           <div>
            <div className="font-medium">{family.name}</div>
            {family.description && (
             <div className="text-sm text-gray-500">{family.description}</div>
            )}
           </div>
           <div className="text-right">
            <div className="text-sm text-gray-600">
             슬롯: {family.slotCount || 0}개
            </div>
            {family.maxMembers === 1 && (
             <div className="text-xs text-gray-500">개인 전용</div>
            )}
           </div>
          </div>
         </div>
        ))}
       </div>
      </div>
     )}

     {showFamilySelection && rootFamilies.length === 0 && (
      <div className="bg-gray-50 p-4 text-center text-gray-500">
       선택한 모피어가 운영하는 루트 모패밀리가 없습니다.
      </div>
     )}

     {/* 슬롯 위임 인터페이스 */}
     {selectedRootFamily && (
      <div className="space-y-4 border-t pt-4">
       <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
         슬롯 위임
         <span className="relative inline-block ml-1 group">
          <span className="text-blue-500 cursor-help text-xs align-super">?</span>
          <div className="hidden group-hover:block absolute bottom-full left-0 w-64 p-3 mb-2 text-xs bg-gray-800 text-white z-10">
           <div className="font-semibold mb-1">슬롯이란?</div>
           영상 10개가 반복되며 재생되는 스케줄을 가지고 있으면, 10개의 슬롯(구좌)을 가지고 있다고 말합니다.
           자신이 가진 슬롯에 영상을 넣어 재생할 수 있는 권한을, 다른 모피어에게 위임할 수 있습니다.<br/><br/>
           이 때, 자신은 더 이상 관리하지 않고, 모든 권한을 넘기는 걸 <span className="text-red-300">독점(Dedicated)</span> 위임이라 하고,
           자신과 공동으로 관리할 수 있도록, 협상하는 양 쪽이 모두 권한을 가지는 걸 <span className="text-blue-300">공유(Shared)</span> 위임이라 합니다.
           <div className="absolute -bottom-2 left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
         </span>
        </label>
        <div className="flex flex-wrap gap-1 justify-start">
         {slotDelegation.map((state, index) => {
          const slotNumber = selectedRootFamilySlots.length > index ? selectedRootFamilySlots[index] : index + 1;
          const slotData = rootFamilySlotsData[index];
          const hasAuthority = slotData?.has_authority || false;
          const isAlreadyDedicated = dedicatedSlots.includes(slotNumber);
          const isDisabled = !hasAuthority || isAlreadyDedicated;

          return (
           <button
            key={index}
            onClick={() => !isDisabled && handleSlotClick(index)}
            disabled={isDisabled}
            className={`w-8 h-5 border transition-colors ${
             !hasAuthority
              ? 'bg-gray-200 border-gray-300 cursor-not-allowed opacity-50'
              : isAlreadyDedicated
              ? 'bg-gray-200 border-gray-300 cursor-not-allowed opacity-50'
              : state === 'none'
              ? 'bg-white border-gray-300 hover:border-gray-400'
              : state === 'dedicated'
              ? 'bg-green-500 border-green-600 text-white text-xs'
              : 'bg-blue-500 border-blue-600 text-white text-xs'
            }`}
            title={
             !hasAuthority ? '권한 없음 - 이미 다른 모패밀리에 위임됨' :
             isAlreadyDedicated ? '이미 다른 모패밀리에 독점 위임됨' :
             state === 'none' ? '클릭: 독점 위임' :
             state === 'dedicated' ? '클릭: 공유 위임' :
             '클릭: 위임 해제'
            }
           >
            {!hasAuthority ? '×' : isAlreadyDedicated ? '×' : state === 'none' ? slotNumber : state === 'dedicated' ? 'D' : 'S'}
           </button>
          );
         })}
        </div>
        <div className="mt-2 text-sm text-gray-600">
         <span className="inline-block w-3 h-3 bg-green-500 mr-1"></span>독점(Dedicated)
         <span className="inline-block w-3 h-3 bg-blue-500 ml-3 mr-1"></span>공유(Shared)
         <span className="ml-3">선택된 슬롯: {inviteForm.proposedSlots}개</span>
         <span className="ml-3 text-xs text-gray-500">(총 {slotDelegation.length}개 슬롯)</span>
        </div>
       </div>

       {/* 슬롯당 일일 제안 가격 */}
       <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
         슬롯당 일일 제안 가격 (원)
        </label>
        <input
         type="number"
         min="0"
         step="1000"
         value={inviteForm.proposedPrice}
         onChange={(e) => setInviteForm({ ...inviteForm, proposedPrice: parseInt(e.target.value) || 0 })}
         className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
       </div>

       {/* 메시지 */}
       <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
         초대 메시지 (선택사항)
        </label>
        <textarea
         rows={3}
         value={inviteForm.message}
         onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
         placeholder="협업 제안에 대한 메시지를 입력하세요..."
         className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
       </div>
      </div>
     )}

     <button
      onClick={inviteMember}
      disabled={!selectedRootFamily || inviteForm.proposedSlots === 0}
      className={`w-full px-5 py-2 ${
       selectedRootFamily && inviteForm.proposedSlots > 0
        ? 'bg-blue-500 hover:bg-blue-600 text-white'
        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
      }`}
     >
      초대 메일 발송
     </button>
    </>
   )}
  </div>
 );
};
